import { useEffect, useRef } from "react";
import analyticsService from "../AnalyticsService";
import { resolveEnvironment } from "../constants";
import { resolvePageName } from "./resolvePageName";

const MIN_ENGAGEMENT_MS = 1000;

const getSessionEventDefaults = () => {
  const pagePath =
    `${globalThis?.location?.pathname || ""}${globalThis?.location?.search || ""}` ||
    null;

  return {
    environment: analyticsService?.context?.environment || resolveEnvironment(),
    page_title:
      resolvePageName(pagePath) ||
      document?.title ||
      globalThis?.location?.pathname ||
      "unknown_page",
    page_path: pagePath,
  };
};

export const useSessionTracker = () => {
  const visibleSinceRef = useRef(document.hidden ? null : Date.now());

  useEffect(() => {
    analyticsService.track(
      "session_start",
      {
        ...getSessionEventDefaults(),
        analytics_session_id: analyticsService.getSessionId(),
      },
      { dedupe: false },
    );

    const trackEngagement = (reason) => {
      if (visibleSinceRef.current === null) {
        return;
      }

      const now = Date.now();
      const engagementTime = now - visibleSinceRef.current;
      visibleSinceRef.current = now;

      if (engagementTime < MIN_ENGAGEMENT_MS) {
        return;
      }

      analyticsService.track(
        "user_engagement",
        {
          ...getSessionEventDefaults(),
          engagement_time_msec: engagementTime,
          engagement_reason: reason,
        },
        { dedupe: false },
      );
    };

    const handleVisibility = () => {
      if (document.hidden) {
        trackEngagement("hidden");
        visibleSinceRef.current = null;
      } else {
        visibleSinceRef.current = Date.now();
      }
    };

    const handleBeforeUnload = () => {
      trackEngagement("beforeunload");
    };

    const handlePageHide = () => {
      trackEngagement("pagehide");
    };

    document.addEventListener("visibilitychange", handleVisibility);
    globalThis.addEventListener("beforeunload", handleBeforeUnload);
    globalThis.addEventListener("pagehide", handlePageHide);

    return () => {
      trackEngagement("unmount");
      document.removeEventListener("visibilitychange", handleVisibility);
      globalThis.removeEventListener("beforeunload", handleBeforeUnload);
      globalThis.removeEventListener("pagehide", handlePageHide);
    };
  }, []);
};
